import React, { useState, useEffect, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector, useDispatch } from "react-redux";
import { Select } from "antd";
import { ReactComponent as DownArrow } from "../../Assets/Icons/greyDownArrow.svg";
import { getRedemptionRates } from "../../services/merchantService";

const displayFilters = {
  lastWeek: "Last Week",
  lastMonth: "Last Month",
  lastYear: "Last Year",
};

const VoucherRedemptionRate = () => {
  const dispatch = useDispatch();
  const { redemptionRates } = useSelector((state) => state?.merchantReducer);
  const [selectedFilter, setSelectedFilter] = useState("lastYear");
  const [filteredSeries, setFilteredSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renderChart, setRenderChart] = useState(true);
  // Create a ref to the chart container for scoped DOM operations
  const chartContainerRef = useRef(null);

  const baseChartOptions = {
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
    },
    colors: [
      "#1E90FF",
      "#FF6347",
      "#32CD32",
      "#FFD700",
      "#8A2BE2",
      "#FF4500",
      "#40E0D0",
      "#FF1493",
      "#7FFF00",
      "#4682B4",
    ],
    chart: {
      fontFamily: "Poppins, sans-serif",
      height: 335,
      type: "area",
      toolbar: {
        show: false,
      },
    },
    responsive: [
      {
        breakpoint: 575,
        options: {
          xaxis: {
            labels: {
              rotate: -45,
              style: {
                fontSize: "10px",
              },
            },
          },
          yaxis: {
            labels: {
              style: {
                fontSize: "10px",
              },
            },
          },
        },
      },
    ],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "none",
      opacity: 0,
    },
    xaxis: {
      type: "category",
      categories: [],
      title: {
        text: `Time Period (Last Year)`,
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (value) => `${value}%`,
      },
      title: {
        text: "Redemption Rate",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (
          value,
          { series, seriesIndex, dataPointIndex, w },
        ) {
          const count =
            w.config.series[seriesIndex].counts?.[dataPointIndex] || 0;
          return `${count} Redemptions (${value}%)`;
        },
      },
    },
  };

  // Scoped chart rerender that only affects this component
  const forceChartRerender = () => {
    // First, destroy the chart by removing it from DOM
    setRenderChart(false);

    // Only remove ApexCharts elements within this component
    if (chartContainerRef.current) {
      const chartElements =
        chartContainerRef.current.querySelectorAll(".apexcharts-canvas");
      chartElements.forEach((element) => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });
    }

    // Give time for cleanup then re-render
    setTimeout(() => {
      setRenderChart(true);
    }, 50);
  };

  const processRedemptionData = (redemptionData) => {
    if (
      !redemptionData ||
      !redemptionData.success ||
      !redemptionData.data?.redemptionRate
    ) {
      setFilteredSeries([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    let { redemptionRate } = redemptionData.data;

    // Handle empty data
    if (
      !redemptionRate ||
      redemptionRate.length === 0 ||
      !redemptionRate[0]?.redemptionRates?.length
    ) {
      setFilteredSeries([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    // Calculate total redemption rate for each offer and sort
    redemptionRate = redemptionRate.map((offer) => ({
      ...offer,
      totalRedemption: offer.redemptionRates.reduce(
        (sum, rate) => sum + parseFloat(rate.rate || 0),
        0,
      ),
    }));
    redemptionRate.sort((a, b) => b.totalRedemption - a.totalRedemption);
    // Take top 5 offers
    const topOffers = redemptionRate.slice(0, 5);

    let finalCategories = [];
    let series = [];

    if (selectedFilter === "lastWeek") {
      const uniquePeriods = new Set();
      topOffers.forEach((offer) => {
        offer.redemptionRates.forEach((rate) => {
          uniquePeriods.add(rate.period);
        });
      });
      const sortedPeriods = Array.from(uniquePeriods).sort((a, b) => {
        const [yA, mA, dA] = a.split("-").map(Number);
        const [yB, mB, dB] = b.split("-").map(Number);
        if (yA !== yB) return yA - yB;
        if (mA !== mB) return mA - mB;
        return dA - dB;
      });

      finalCategories = sortedPeriods.map((period) => {
        if (typeof period !== "string" || !period.includes("-")) return period || "";
        const parts = period.split("-");
        const day = parts[2].padStart(2, "0");
        const monthName = new Date(
          `${parts[0]}-${parts[1].padStart(2, "0")}-01`,
        )
          .toLocaleString("default", { month: "short" })
          .toUpperCase();
        return `${day} ${monthName}`;
      });

      series = topOffers.map((offer) => {
        const counts = [];
        const rates = sortedPeriods.map((period) => {
          const found = offer.redemptionRates.find((r) => r.period === period);
          counts.push(found ? parseInt(found.count) || 0 : 0);
          return found ? parseFloat(found.rate) : 0;
        });
        return { name: offer.offerName, data: rates, counts };
      });
    } else if (selectedFilter === "lastMonth") {
      const formatWeekDateRange = (start, end) => {
        const startDay = start.getDate();
        const endDay = end.getDate();
        const startMonth = start.toLocaleString("default", { month: "short" });
        const endMonth = end.toLocaleString("default", { month: "short" });
        if (startMonth === endMonth) {
          return `(${startDay}-${endDay} ${startMonth})`;
        } else {
          return `(${startDay} ${startMonth} - ${endDay} ${endMonth})`;
        }
      };

      const weeks = [];
      const currentDate = new Date();
      for (let i = 0; i < 4; i++) {
        const daysInWeek = i === 3 ? 9 : 7;
        const end = new Date(currentDate);
        end.setDate(currentDate.getDate() - i * 7);
        const start = new Date(currentDate);
        start.setDate(currentDate.getDate() - i * 7 - daysInWeek + 1);

        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        weeks.unshift({
          label: `W${4 - i}`,
          start,
          end,
        });
      }

      finalCategories = weeks.map((week) => {
        return [week.label, formatWeekDateRange(week.start, week.end)];
      });

      series = topOffers.map((offer) => {
        const data = weeks.map((week) => {
          let weekCount = 0;
          let offerTotalCount = 0;

          offer.redemptionRates.forEach((rate) => {
            const parts = rate.period.split("-");
            if (parts.length === 3) {
              const year = parseInt(parts[0]);
              const month = parseInt(parts[1]) - 1;
              const day = parseInt(parts[2]);
              const rateDate = new Date(year, month, day);
              rateDate.setHours(0, 0, 0, 0);

              const count = parseInt(rate.count) || 0;
              offerTotalCount += count;

              if (rateDate >= week.start && rateDate <= week.end) {
                weekCount += count;
              }
            }
          });

          const rate =
            offerTotalCount > 0 ? (weekCount / offerTotalCount) * 100 : 0;
          return { rate: parseFloat(rate.toFixed(2)), count: weekCount };
        });

        const rates = data.map((d) => d.rate);
        const counts = data.map((d) => d.count);

        return { name: offer.offerName, data: rates, counts };
      });
    } else {
      const uniquePeriods = new Set();
      topOffers.forEach((offer) => {
        offer.redemptionRates.forEach((rate) => {
          uniquePeriods.add(rate.period);
        });
      });
      let sortedPeriods = Array.from(uniquePeriods).sort((a, b) => {
        const [yA, mA] = a.split("-").map(Number);
        const [yB, mB] = b.split("-").map(Number);
        if (yA !== yB) return yA - yB;
        return mA - mB;
      });
      sortedPeriods = sortedPeriods.slice(-6);

      finalCategories = sortedPeriods.map((period) => {
        if (typeof period !== "string" || !period.includes("-")) return period || "";
        const parts = period.split("-");
        const monthName = new Date(
          `${parts[0]}-${parts[1].padStart(2, "0")}-01`,
        )
          .toLocaleString("default", { month: "short" })
          .toUpperCase();
        return `${monthName} ${parts[0]}`;
      });

      series = topOffers.map((offer) => {
        const counts = [];
        const rates = sortedPeriods.map((period) => {
          const found = offer.redemptionRates.find((r) => r.period === period);
          counts.push(found ? parseInt(found.count) || 0 : 0);
          return found ? parseFloat(found.rate) : 0;
        });
        return { name: offer.offerName, data: rates, counts };
      });
    }

    setCategories(finalCategories);
    setFilteredSeries(series);
    setLoading(false);
  };

  const handleFilterChange = (filterValue) => {
    console.log("filterValue", filterValue);
    setSelectedFilter(filterValue);
    setLoading(true);
    dispatch(getRedemptionRates({ timePeriod: filterValue }));
  };

  useEffect(() => {
    if (redemptionRates) {
      processRedemptionData(redemptionRates);
    }
  }, [redemptionRates]);

  useEffect(() => {
    // Cleanup on unmount - scoped to only this component
    return () => {
      if (chartContainerRef.current) {
        const chartElements =
          chartContainerRef.current.querySelectorAll(".apexcharts-canvas");
        chartElements.forEach((element) => {
          if (element.parentNode) {
            element.parentNode.removeChild(element);
          }
        });
      }
    };
  }, []);

  const filterOptions = [
    { label: "Last Week", value: "lastWeek" },
    { label: "Last Month", value: "lastMonth" },
    { label: "Last Year", value: "lastYear" },
  ];

  const isLastWeek = selectedFilter === "lastWeek";

  // Generate a new chart options object each time to avoid reference issues
  const getChartOptions = () => {
    return {
      ...baseChartOptions,
      xaxis: {
        ...baseChartOptions.xaxis,
        categories: categories,
        title: {
          ...baseChartOptions.xaxis.title,
          text: `Time Period (${displayFilters[selectedFilter] || "Last Year"})`,
        },
      },
    };
  };

  return (
    <div className="rounded-sm border border-stroke bg-white px-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 summary offersChart">
      <div className="flex flex-col gap-4 xl:flex-row xl:gap-0 justify-between w-full mt-4">
        <span className="text-[18px] sm:text-[20px] font-poppins font-[600] text-[black] leading-tight flex-1">
          Offer Redemption Rate
        </span>
        <Select
          value={selectedFilter}
          className={
            "border border-[#00ADEF] rounded-md pr-3 text-lg font-medium outline-none h-[40px]"
          }
          suffixIcon={<DownArrow />}
          options={filterOptions}
          defaultValue={selectedFilter}
          onChange={handleFilterChange}
          dropdownMatchSelectWidth={false}
          style={{ minWidth: "100px", maxWidth: "200px" }}
        />
      </div>

      <div
        ref={chartContainerRef}
        className="w-full flex items-center justify-center"
      >
        <div id="chartOne" className="mt-4 w-full h-[430px]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500 text-lg font-medium">
                Loading...
              </div>
            </div>
          ) : filteredSeries.length > 0 &&
            filteredSeries[0]?.data?.length > 0 &&
            renderChart ? (
            <ReactApexChart
              key={`chart-${selectedFilter}-${Date.now()}`}
              options={getChartOptions()}
              series={JSON.parse(JSON.stringify(filteredSeries))} // Deep clone to avoid reference issues
              type="area"
              height={410}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500 text-lg font-medium">
                {!renderChart
                  ? "Updating Chart..."
                  : "No Redemption Data Found"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoucherRedemptionRate;
